/**
 * <persona-explorer> — tabbed audience explorer.
 * Autonomous custom element (theme convention, see age-verifier.js).
 * Self-inits per instance, so Shopify theme-editor re-renders reconnect automatically.
 *
 * INSTALL: this file must be uploaded to the theme's `assets/`. The section
 * emits <script src="{{ 'persona-explorer.js' | asset_url }}" defer>. If the
 * asset is missing the tabs render but do not switch.
 */
if (!customElements.get('persona-explorer')) {
  class PersonaExplorer extends HTMLElement {
    connectedCallback() {
      // Children may not be parsed yet if the element is upgraded early; retry next frame.
      if (!this.init()) {
        requestAnimationFrame(() => this.init());
      }
    }

    init() {
      this.tabs = Array.from(this.querySelectorAll('.persona-explorer__tab'));
      this.panes = Array.from(this.querySelectorAll('.persona-explorer__pane'));
      if (!this.tabs.length || !this.panes.length) return false;
      if (this.bound) return true;
      this.bound = true;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onClick);
        tab.addEventListener('keydown', this.onKeydown);
      });
      return true;
    }

    disconnectedCallback() {
      (this.tabs || []).forEach((tab) => {
        tab.removeEventListener('click', this.onClick);
        tab.removeEventListener('keydown', this.onKeydown);
      });
      this.bound = false;
    }

    onClick(event) {
      this.select(this.tabs.indexOf(event.currentTarget));
    }

    onKeydown(event) {
      const current = this.tabs.indexOf(event.currentTarget);
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % this.tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + this.tabs.length) % this.tabs.length;
      if (next === null) return;
      event.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }

    select(index) {
      if (index < 0 || index >= this.panes.length) return;
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });
      this.panes.forEach((pane, i) => pane.classList.toggle('is-active', i === index));

      // Retrigger entrance animation on user switch only.
      const pane = this.panes[index];
      pane.classList.remove('persona-explorer__pane--anim');
      void pane.offsetWidth;
      pane.classList.add('persona-explorer__pane--anim');
    }
  }
  customElements.define('persona-explorer', PersonaExplorer);
}
